<script lang="ts">
  /**
   * Eine Listenzeile, der man ansieht, was sie kann.
   *
   * ## Das Problem
   *
   * Ein Tagebucheintrag ließ sich öffnen, indem man die Zeile antippte. Nichts
   * deutete darauf hin: kein Chevron, keine Hervorhebung, keine definierte
   * Trefferfläche. Wer es nicht wusste, hielt die Liste für eine Anzeige.
   *
   * Der Fall ist behoben, das Muster lebt weiter: Die Auswahlzeilen im
   * Umplanen-Assistenten sind Knöpfe ohne Andeutung, die Haken-Zeile im
   * Tagesplan sieht aus wie Text, die Ausweichquartier-Blöcke sehen aus wie
   * Zeilen und sind keine.
   *
   * ## Die Regel
   *
   * **Was sich anfassen lässt, sieht danach aus. Was sich nicht anfassen lässt,
   * sieht nicht danach aus.** Vier Fassungen, äußerlich unterscheidbar:
   *
   * | `fassung` | Woran man sie erkennt |
   * |---|---|
   * | `tippbar` | Chevron rechts, Hover hebt die Fläche, ganze Zeile ist Trefferfläche |
   * | `starr` | kein Chevron, kein Hover, kein Zeigefinger — eine Anzeige |
   * | `gewaehlt` | Akzentrand, Akzentfläche, Chevron bleibt |
   * | `haken` | Ablaufpunkt: der Kreis links ist die Andeutung |
   *
   * ## Nebenhandlungen
   *
   * Umsortieren und Entfernen stehen im Bestand als vier Symbolknöpfe je
   * Stationszeile, deutlich unter 24 px und so dicht, dass „überspringen" und
   * „entfernen" zu verwechseln sind. Hier sind sie `--tippziel-neben` groß und
   * erscheinen bei Hover bzw. Fokus — auf Zeigegeräten. Auf Berührung stehen
   * sie dauerhaft, weil es dort kein Hover gibt, und das Maß springt dort auf
   * die vollen `--tippziel` zurück.
   *
   * Die Größe steht **hier** und nicht am Aufrufer: Sie ist eine Eigenschaft
   * der Nebenhandlungen und keine der Stationsliste. Stünde sie je Liste, hätte
   * das Reiseregister andere Knöpfe als der Tagesplan — und niemand wüsste,
   * welche der beiden Größen die gemeinte ist.
   *
   * ## Anders als in der Vorlage
   *
   * - Hover und Fokus laufen über CSS (`:hover`, `:focus-visible`,
   *   `:focus-within`) statt über zwei React-Zustände. Zustand für etwas zu
   *   halten, das der Browser ohnehin kennt, kostet bei einer Liste mit
   *   vierzig Zeilen vierzig Rerender — und die Berührungsfassung fällt dabei
   *   als `@media (hover: none)` von selbst ab.
   * - Der Chevron ist `Zeichen name="weiter"` und kein „›". Der Zeichensatz
   *   des Hauses gibt es genau dafür: Unicode-Pfeile sind je nach Schrift
   *   unterschiedlich breit und schwer.
   * - Der Haken ist `Zeichen name="haken"` statt „✓", aus demselben Grund.
   */
  import type { Snippet } from 'svelte';
  import Zeichen from '../Zeichen.svelte';
  import { istTippbar, zeigtChevron, type Zeilenfassung } from './muster';

  interface Props {
    fassung?: Zeilenfassung;
    /** Nummer, Uhrzeit, Kategoriepunkt — links vor dem Titel. */
    fuehrung?: Snippet;
    titel: string;
    unterzeile?: string | null;
    zusatz?: string | null;
    /** Rechts neben dem Text — Marke, Preis, Bewertung. */
    rechts?: Snippet;
    /** Symbolknöpfe in 44-px-Zielen (`Zeichenknopf gross`). */
    nebenhandlungen?: Snippet;
    /** Nur bei `fassung="haken"`. */
    abgehakt?: boolean;
    /** Übersprungene Station: durchgestrichen und gedimmt, aber sichtbar. */
    gedaempft?: boolean;
    onclick?: () => void;
    children?: Snippet;
  }
  let {
    fassung = 'tippbar',
    fuehrung,
    titel,
    unterzeile = null,
    zusatz = null,
    rechts,
    nebenhandlungen,
    abgehakt = false,
    gedaempft = false,
    onclick,
    children
  }: Props = $props();

  const tippbar = $derived(istTippbar(fassung, Boolean(onclick)));
  const chevron = $derived(zeigtChevron(fassung, Boolean(onclick)));
  const haken = $derived(fassung === 'haken');
</script>

<!-- Der Inhalt steht einmal und wird in Knopf oder Feld gesetzt. Zweimal
     geschrieben liefe er auseinander — genau das ist im Bestand passiert. -->
{#snippet inhalt()}
  {#if haken}
    <span class="kreis" class:an={abgehakt} aria-hidden="true">
      {#if abgehakt}<Zeichen name="haken" groesse={13} strichstaerke={2.4} />{/if}
    </span>
  {:else if fuehrung}
    {@render fuehrung()}
  {/if}

  <span class="text">
    <span
      class="titel"
      class:betont={fassung === 'gewaehlt'}
      class:erledigt={gedaempft || (haken && abgehakt)}>{titel}</span
    >
    {#if unterzeile}<span class="unterzeile">{unterzeile}</span>{/if}
    <!-- Der Zusatz darf umbrechen statt abzuschneiden: Er trägt Begründungen
         („Der Ausdruck ist der Rückfall, wenn kein Netz da ist"), und ein
         abgeschnittener Grund ist kein Grund. -->
    {#if zusatz}<span class="zusatz">{zusatz}</span>{/if}
    {#if children}{@render children()}{/if}
  </span>

  {#if rechts}<span class="rechts">{@render rechts()}</span>{/if}

  {#if chevron}
    <span class="chevron" aria-hidden="true"><Zeichen name="weiter" groesse={15} /></span>
  {/if}
{/snippet}

<div class="aussen">
  {#if tippbar}
    <button
      type="button"
      class="zeile {fassung}"
      aria-pressed={haken ? abgehakt : undefined}
      {onclick}
    >
      {@render inhalt()}
    </button>
  {:else}
    <div class="zeile {fassung}">
      {@render inhalt()}
    </div>
  {/if}

  {#if nebenhandlungen}
    <!-- Klicks auf die Nebenhandlungen dürfen die Zeile nicht mitauslösen:
         Sonst löscht der Papierkorb die Station und wählt sie im selben Moment
         aus. Hier statt an jedem Aufrufer, weil es für jede Liste gilt. -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <span class="neben" onclick={(e) => e.stopPropagation()}>
      {@render nebenhandlungen()}
    </span>
  {/if}
</div>

<style>
  /* min-width: 0 — sonst kann die Zeile in einer Rasterspalte nicht unter ihre
     Inhaltsbreite schrumpfen und schiebt die Spalte auf.

     flex-wrap — die Antwort auf schmale Spalten. Vier Nebenhandlungen sind
     rund 134 px, und in einem engen Panel quetschten sie die Zeile auf
     Handtuchbreite: „Bernau …" statt eines Ortsnamens, während rechts vier
     Knöpfe in der Leere schwebten. Ein Titel, den man nicht lesen kann, ist
     schlimmer als eine zweite Zeile — deshalb behält die Zeile eine
     Mindestbreite, und wird es enger, rutschen die Nebenhandlungen darunter
     und stellen sich rechts an.

     Die Schwelle dafür ist die Mindestbreite und **nicht** die natürliche
     Textbreite. Das stand zwar so im Absatz darüber, umgesetzt war es nicht:
     Mit `flex-basis: auto` rechnet der Umbruch mit der ungekürzten Zeile, und
     eine einzige lange Adresse schickte die Knöpfe nach unten — auch dort, wo
     zwei Handbreit Platz übrig waren. Seit die Basis die Mindestbreite ist,
     bricht die Zeile erst um, wenn wirklich zu wenig Platz ist; darüber wächst
     sie über `flex-grow` wie zuvor. */
  .aussen {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    /* Eng an der Zeile: Die Knöpfe sind Werkzeuge **dieser** Station. Der
       Abstand zur nächsten Zeile ist ein Vielfaches davon (siehe die Liste,
       die dieses Muster setzt) — Zugehörigkeit ist eine Abstandsfrage, und
       zwar eine der Verhältnisse, nicht der absoluten Pixel. */
    gap: var(--raum-1);
    min-width: 0;
  }

  .zeile {
    display: flex;
    align-items: flex-start;
    gap: var(--raum-5);
    flex: 1 1 min(14rem, 100%);
    width: auto;
    min-width: min(14rem, 100%);
    text-align: left;
    min-height: var(--dichte-zeile);
    padding: var(--dichte-innen) var(--raum-5);
    /* border-box, weil das Bauteil überall sitzt: ohne dies addieren sich
       Polsterung (2×10 px) und Rand (2×1 px) auf die 100 % Breite und die
       Zeile läuft aus der schmalen Panelspalte heraus. */
    box-sizing: border-box;
    border: 1px solid transparent;
    border-radius: var(--radius-klein);
    background: var(--surface);
    font: inherit;
    color: var(--text);
    cursor: default;
    transition: background-color 120ms ease-out, border-color 120ms ease-out;
  }

  button.zeile {
    cursor: pointer;
  }
  button.zeile:hover,
  button.zeile:focus-visible {
    background: var(--surface-2);
    border-color: var(--border-stark);
  }
  button.zeile:focus-visible {
    outline: var(--fokus-ring);
    outline-offset: var(--fokus-abstand);
  }

  .gewaehlt {
    border-color: var(--accent);
    background: var(--accent-weich);
  }
  button.zeile.gewaehlt:hover,
  button.zeile.gewaehlt:focus-visible {
    background: var(--accent-weich);
    border-color: var(--accent);
  }

  /* Der Kreis ist die Andeutung des Hakens — vorher sah die Zeile aus wie Text.
     Er heißt `kreis` und nicht `haken`: Die Fassung steht als Klasse an der
     Zeile selbst (`class="zeile haken"`), und zwei gleichnamige Klassen im
     selben Baum ließen die ganze Zeile auf 19×19 px schrumpfen. */
  .kreis {
    flex: none;
    width: 19px;
    height: 19px;
    margin-top: 1px;
    border-radius: var(--radius-rund);
    border: 2px solid var(--border-stark);
    background: transparent;
    color: var(--text-auf-akzent);
    display: grid;
    place-items: center;
    line-height: 1;
  }
  .kreis.an {
    border-color: var(--zustand-frei);
    background: var(--zustand-frei);
  }

  .text {
    flex: 1;
    /* Ohne min-width: 0 weigert sich ein Flex-Kind zu schrumpfen, und ein
       langer Stationsname schiebt die Nebenhandlungen aus der Leiste. */
    min-width: 0;
    display: grid;
    gap: 1px;
  }

  .titel {
    font-size: var(--fs-panel);
    font-weight: var(--gw-normal);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .titel.betont {
    font-weight: var(--gw-halb);
  }

  /* Übersprungen und abgehakt heißt: bleibt sichtbar, zählt aber nicht.
     Ausblenden würde die Frage „wo ist die Station hin?" aufwerfen, die man
     gerade beantwortet hat. */
  .titel.erledigt {
    color: var(--text-dim);
    text-decoration: line-through;
  }

  .unterzeile {
    font-size: var(--fs-klein);
    color: var(--text-dim);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .zusatz {
    font-size: var(--fs-fein);
    color: var(--text-schwach);
    min-width: 0;
    overflow-wrap: anywhere;
  }

  /* Die Marke rechts liegt auf der **Mittellinie** der Zeile, nicht an ihrer
     Oberkante — dieselbe Ausrichtung wie der Chevron drei Regeln tiefer, und
     aus demselben Grund: Beides sind Zeichen am rechten Rand und keine
     Textzeilen, die mit dem Titel fluchten müssten.

     Ohne diese Zeile hing die Marke an `align-items: flex-start` der Zeile
     fest, während die Nebenhandlungen daneben (`.neben`) zentriert stehen.
     Bei einer einzeiligen Zeile fällt beides zusammen; sobald Unterzeile oder
     Zusatz dazukommen, standen Pille und Knopfreihe sichtbar versetzt — in
     der Reiseliste am deutlichsten, wo unter dem Namen noch das Datum und an
     gefahrenen Reisen die Vergleichszeile steht (Befund 31.8.2026). */
  /* ── Und sie darf nachgeben ───────────────────────────────────────
     `flex: none` stand hier bis zum 31.8.2026 und war für kurze Marken
     richtig — für eine lange die zweite Ausgabe desselben Fehlers wie am
     Kapitelkopf: Die Zugangs-Pille eines Fuß-Abstechers trägt „zu Fuß ab
     Jeep-Tour in den aktiven Bruch", `white-space: nowrap`, und schob sich
     samt Chevron aus der Spalte.

     `flex: 0 1 auto` statt `none`: Die Marke behält ihre natürliche Breite
     (`flex-basis: auto`, kein Wachsen) und gibt **nur unter Druck** nach —
     kurze Pillen sehen aus wie vorher. Dass sie es ist, die nachgibt, und
     nicht der Name daneben, folgt aus der Zeile selbst: `.text` hat
     `flex-basis: 0`, kann also nichts abgeben; die Verknappung landet
     zwangsläufig hier. Genau die richtige Reihenfolge — der Ortsname ist die
     Auskunft, die Marke die Beigabe.

     **Wie** sie nachgibt, entscheidet der Inhalt und nicht dieses Bauteil:
     Hier steht mal eine Marke, mal ein Preis, mal ein Knopf. Die Pille der
     Stationsliste kürzt mit Auslassungspunkten (siehe `StopList.svelte`). */
  .rechts {
    flex: 0 1 auto;
    min-width: 0;
    align-self: center;
  }

  .chevron {
    flex: none;
    color: var(--text-schwach);
    line-height: 1;
    display: flex;
    align-items: center;
    align-self: center;
  }
  button.zeile:hover .chevron,
  button.zeile:focus-visible .chevron {
    color: var(--accent);
  }

  .neben {
    flex: none;
    display: flex;
    align-items: center;
    gap: var(--raum-1);
    /* Die Trefferfläche wird hier gesetzt und nicht am Knopf: `Zeichenknopf`
       kennt zwei Größen, und keine davon ist die richtige für eine dichte
       Liste.

       Als geerbte Eigenschaft und nicht mehr als `.neben :global(button)`.
       Die alte Fassung stand richtig da und wirkte nie: `.neben` plus
       Elementname wiegt weniger als `button.gross` im `Zeichenknopf`, also
       blieb jeder Knopf bei 44 px. Die Gruppe war damit 182 statt 134 px
       breit, und weil zwischen zwei 44-px-Feldern mit 18-px-Zeichen rund
       26 px Luft stehen, sah sie aus wie vier weit verstreute Knöpfe — in
       der schmalen Planerspalte rutschte sie obendrein unter die Karte.
       Ein Custom Property kennt keinen Spezifitätsstreit; es erbt und gilt.

       Am Finger fällt `--tippziel-neben` auf die vollen 44 px zurück (siehe
       token/raum.css) — der Griff gibt hier weiter, was dort entschieden
       wird, und entscheidet nichts selbst. */
    --zeichenknopf-gross: var(--tippziel-neben);
    /* Rechtsbündig — auf einer Linie mit der Zeile ohnehin, und wenn sie in
       der Enge darunterrutschen, stehen sie unter dem rechten Rand statt
       verloren unter der Nummer. */
    margin-left: auto;
    /* Auf Zeigegeräten gedämpft, bis die Zeile angesteuert wird — nicht ganz
       ausgeblendet, weil sonst niemand ahnt, dass es sie gibt. */
    opacity: 0.35;
    transition: opacity 120ms ease-out;
  }
  .aussen:hover .neben,
  .aussen:focus-within .neben {
    opacity: 1;
  }
  /* Auf Berührung gibt es kein Hover — dort stehen sie immer. */
  @media (hover: none) {
    .neben {
      opacity: 1;
    }
  }
</style>
